import type { MaybeRefOrGetter } from 'vue';

import { isInstagramBrowserAgent } from '~/utils';

/**
 * Nome da animação declarada em `app/assets/css/main.css`. Navegadores não
 * emitem evento próprio ao aplicar autofill, então uma animação sem efeito
 * visual é usada como gatilho observável.
 */
export const AUTOFILL_ANIMATION_NAME = 'contact-form-autofill-start';

/**
 * `:autofill` é o seletor padrão e `:-webkit-autofill` o legado. Navegadores
 * que não conhecem um deles lançam erro em `matches`, por isso são testados
 * separadamente.
 */
const AUTOFILL_PSEUDO_SELECTORS = [':autofill', ':-webkit-autofill'];

export const CONTACT_AUTOFILL_FIELDS = ['name', 'phone', 'email'] as const;

export type ContactAutofillField = (typeof CONTACT_AUTOFILL_FIELDS)[number];

export interface ContactFormAutofillState {
	hasDetectedAutofill: boolean;
	autofilledFields: ContactAutofillField[];
	isInstagramBrowser: boolean;
}

export interface ContactFormAutofillSnapshot extends ContactFormAutofillState {
	/**
	 * Representa "autofill detectado em uma sessão dentro do navegador do
	 * Instagram". Não afirma que o preenchimento veio do Instagram: em iOS não
	 * é possível distinguir o autofill da Meta do AutoFill do próprio sistema.
	 */
	isAutofillInInstagramSession: boolean;
}

function isContactAutofillField(value: string): value is ContactAutofillField {
	return CONTACT_AUTOFILL_FIELDS.includes(value as ContactAutofillField);
}

function isElementAutofilled(element: Element): boolean {
	return AUTOFILL_PSEUDO_SELECTORS.some(selector => {
		try {
			return element.matches(selector);
		} catch {
			return false;
		}
	});
}

export function useContactFormAutofill() {
	const state = useState<ContactFormAutofillState>(
		'contactFormAutofill',
		() => ({
			hasDetectedAutofill: false,
			autofilledFields: [],
			isInstagramBrowser: false
		})
	);

	const isAutofillInInstagramSession = computed(
		() => state.value.hasDetectedAutofill && state.value.isInstagramBrowser
	);

	function registerAutofilledField(field: string) {
		if (!isContactAutofillField(field)) return;

		state.value.hasDetectedAutofill = true;

		if (state.value.autofilledFields.includes(field)) return;

		state.value.autofilledFields = [...state.value.autofilledFields, field];
	}

	function detectBrowserContext() {
		if (!import.meta.client) return;

		state.value.isInstagramBrowser = isInstagramBrowserAgent();
	}

	/**
	 * Fallback para navegadores que não disparam `animationstart` no autofill,
	 * notadamente Safari/iOS: lê a pseudo-classe diretamente dos campos.
	 */
	function syncAutofilledFields(root: HTMLElement | null | undefined) {
		if (!import.meta.client || !root) return;

		const fields = root.querySelectorAll<HTMLInputElement>('input[name]');

		fields.forEach(field => {
			if (!isElementAutofilled(field)) return;

			registerAutofilledField(field.name);
		});
	}

	function observeAutofill(
		target: MaybeRefOrGetter<HTMLElement | null | undefined>
	) {
		if (!import.meta.client) return;

		useEventListener(target, 'animationstart', (event: AnimationEvent) => {
			if (event.animationName !== AUTOFILL_ANIMATION_NAME) return;

			const field = event.target as HTMLInputElement | null;

			if (!field?.name) return;

			registerAutofilledField(field.name);
		});

		useEventListener(target, 'change', () => {
			syncAutofilledFields(toValue(target));
		});
	}

	function resetAutofillState() {
		state.value.hasDetectedAutofill = false;
		state.value.autofilledFields = [];
	}

	function getAutofillSnapshot(): ContactFormAutofillSnapshot {
		return {
			hasDetectedAutofill: state.value.hasDetectedAutofill,
			autofilledFields: [...state.value.autofilledFields],
			isInstagramBrowser: state.value.isInstagramBrowser,
			isAutofillInInstagramSession: isAutofillInInstagramSession.value
		};
	}

	return {
		state,
		isAutofillInInstagramSession,
		registerAutofilledField,
		detectBrowserContext,
		syncAutofilledFields,
		observeAutofill,
		resetAutofillState,
		getAutofillSnapshot
	};
}
